import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import stressAnxietyHeroImage from './blogimg/howToReduceStressAndAnxiety/stress-anxiety-hero.jpeg';
import understandingStressImage from './blogimg/howToReduceStressAndAnxiety/understanding-stress.jpeg';
import brainScienceImage from './blogimg/howToReduceStressAndAnxiety/brain-science.jpeg';
import mindfulnessImage from './blogimg/howToReduceStressAndAnxiety/mindfulness.jpeg';
import exerciseImage from './blogimg/howToReduceStressAndAnxiety/exercise.jpeg';
import cbtImage from './blogimg/howToReduceStressAndAnxiety/cbt.jpeg';
import socialSupportImage from './blogimg/howToReduceStressAndAnxiety/social-support.jpeg';
import lifestyleChangesImage from './blogimg/howToReduceStressAndAnxiety/lifestyle-changes.jpeg';
import ashwagandhaImage from './blogimg/howToReduceStressAndAnxiety/ashwagandha.png';
import mindfulnessMeditationImage from './blogimg/howToReduceStressAndAnxiety/mindfulness-meditation.jpeg';
import progressiveMuscleRelaxationImage from './blogimg/howToReduceStressAndAnxiety/progressive-muscle-relaxation.jpeg';
import deepBreathingExercisesImage from './blogimg/howToReduceStressAndAnxiety/deep-breathing-exercises.jpeg';
import cognitiveRestructuringImage from './blogimg/howToReduceStressAndAnxiety/cognitive-restructuring.jpeg';
import exposureTherapyImage from './blogimg/howToReduceStressAndAnxiety/exposure-therapy.jpeg';
import problemSolvingImage from './blogimg/howToReduceStressAndAnxiety/problem-solving.jpeg';
import balancedDietImage from './blogimg/howToReduceStressAndAnxiety/balanced-diet.jpeg';
import adequateSleepImage from './blogimg/howToReduceStressAndAnxiety/adequate-sleep.jpeg';
import timeManagementImage from './blogimg/howToReduceStressAndAnxiety/time-management.jpeg';
import natureGreenSpacesImage from './blogimg/howToReduceStressAndAnxiety/nature-green-spaces.jpeg';

/**
 * Helper function to map Firestore filenames to local image imports.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'stress-anxiety-hero.jpeg':
      return stressAnxietyHeroImage;
    case 'understanding-stress.jpeg':
      return understandingStressImage;
    case 'brain-science.jpeg':
      return brainScienceImage;
    case 'mindfulness.jpeg':
      return mindfulnessImage;
    case 'exercise.jpeg':
      return exerciseImage;
    case 'cbt.jpeg':
      return cbtImage;
    case 'social-support.jpeg':
      return socialSupportImage;
    case 'lifestyle-changes.jpeg':
      return lifestyleChangesImage;
    case 'ashwagandha.png':
      return ashwagandhaImage;
    case 'mindfulness-meditation.jpeg':
      return mindfulnessMeditationImage;
    case 'progressive-muscle-relaxation.jpeg':
      return progressiveMuscleRelaxationImage;
    case 'deep-breathing-exercises.jpeg':
      return deepBreathingExercisesImage;
    case 'cognitive-restructuring.jpeg':
      return cognitiveRestructuringImage;
    case 'exposure-therapy.jpeg':
      return exposureTherapyImage;
    case 'problem-solving.jpeg':
      return problemSolvingImage;
    case 'balanced-diet.jpeg':
      return balancedDietImage;
    case 'adequate-sleep.jpeg':
      return adequateSleepImage;
    case 'time-management.jpeg':
      return timeManagementImage;
    case 'nature-green-spaces.jpeg':
      return natureGreenSpacesImage;
    default:
      return null;
  }
}

const BlogReduceStresAnxietyPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "How to Reduce Stress and Anxiety: Scientifically Proven Strategies";

    // Helper function to add/update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Discover evidence-based methods for managing stress and anxiety, including mindfulness, exercise, and Ashwagandha supplementation."
    );
    setMetaTag(
      "keywords",
      "reduce stress, anxiety relief, mindfulness, ashwagandha, exercise, CBT, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "How to Reduce Stress & Anxiety: Proven Techniques",
      true
    );
    setMetaTag(
      "og:description",
      "Discover science-backed techniques to effectively reduce stress and anxiety, including relaxation methods, mindfulness, and lifestyle changes.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/stress-anxiety-hero.jpeg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/how-to-reduce-stress-and-anxiety",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
      // Remove or reset meta tags if needed
    };
  }, []);

  // 2️⃣ Fetch Article Data from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Firestore path: /products/blog/HowToReduceStressAndAnxiety
        const colRef = collection(db, 'products', 'blog', 'HowToReduceStressAndAnxiety');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No blog document found for HowToReduceStressAndAnxiety');
        }
      } catch (error) {
        // console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Render Block Function
  const renderBlock = (block, index) => {
    const { tag, text } = block;
    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1">
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2">
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3">{text}</h3>
          </React.Fragment>
        );
      case 'h4':
        return <h4 key={index}>{text}</h4>;
      case 'p':
        return (
          <p key={index} className="recipe-p">
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul">
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol">
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === 'object') {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return <div key={index}>{text}</div>;
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 4️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 from Firestore data (if any) */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}
        {/* Render each section */}
        {article.sections &&
          article.sections.map((section, sectionIndex) => (
            <section key={sectionIndex}>
              {section.blocks &&
                section.blocks.map((block, blockIndex) =>
                  renderBlock(block, blockIndex)
                )}
            </section>
          ))}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() => (window.location.href = 'https://purehealthnexus.com/ashwagandha')}
        >
          See Top-Rated Ashwagandha Here
        </button>
      </div>
    </div>
  );
};

export default BlogReduceStresAnxietyPage;
